import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose, faInfo, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import StatusCell from "./StatusCell";
import { UserGroupColumn } from "../../People/useColumns";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import { useCustomFields } from "../../Settings/CustomFields/context";
import { useAccess } from "../../../utils/hooks/access";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import {
    getSettingsOrgResourceSearchFilters,
    getSettingsResourceSearchFilters,
    renderOrgLevelResourceLabel,
    renderSettingsResourceLabel
} from "../../../utils/helpers/settings";
import { getPrimaryActiveResource, renderUserName } from "../../../utils/helpers/user";

const columnHelper = createColumnHelper();

export const useColumns = ({ openDetailsModal, openApproveDenyModal, selectedStatus }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { hasAccess: canApprove } = useAccess("timeSheet.canApproveRetro");
    const { groups } = useGroups();
    const { peopleCustomFields } = useCustomFields();

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("user.name", {
                enableColumnFilter: true,
                header: t("name"),
                cell: (info) => {
                    const value = info.row.original.user;
                    return value ? renderUserName(value) : "--";
                }
            }),
            columnHelper.accessor("user.employeeNumber", {
                enableColumnFilter: true,
                header: t("employee-number"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? value : "--"
                }
            }),
            columnHelper.accessor("payGroup", {
                enableColumnFilter: true,
                header: t("pay-group"),
                cell: (info) => {
                    const value = info.getValue();
                    return (typeof value !== "string" && value) ? renderSettingsResourceLabel(value) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/pay-group"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.locations.locationId", {
                enableColumnFilter: true,
                header: t("location"),
                cell: (info) => {
                    const { locations } = info.row.original.user;
                    const location = getPrimaryActiveResource(locations);
                    return location ? renderSettingsResourceLabel(location) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/locations"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.jobs.jobId", {
                enableColumnFilter: true,
                header: t("job-title"),
                cell: (info) => {
                    const { jobs } = info.row.original.user;
                    const job = getPrimaryActiveResource(jobs);
                    return job ? renderSettingsResourceLabel(job) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/jobs"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.salaryOrHourly", {
                enableColumnFilter: true,
                header: t("salary-or-hourly"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? value : "--";
                },
                filterType: "select",
                Filter: (
                    <Select>
                        <Select.Option value="S">{t("salaried")}</Select.Option>
                        <Select.Option value="H">{t("hourly")}</Select.Option>
                    </Select>
                )
            }),
            columnHelper.accessor("user.calculationGroup", {
                enableColumnFilter: true,
                header: t("calculation-group"),
                cell: ({ row: { original }}) => {
                    const value = original?.user?.calculationGroupRes;
                    return value ? renderSettingsResourceLabel(value) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/calculation-group"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.userGroups", {
                enableColumnFilter: true,
                header: t("groups"),
                cell: (info) => {
                    const { user: { userGroupsRes }, id } = info.row.original;
                    return <UserGroupColumn userGroups={userGroupsRes} id={id} />;
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/user-group"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.employeeType", {
                enableColumnFilter: true,
                header: t("employee-type"),
                cell: ({ row: { original }}) => {
                    const value = original?.user?.employeeTypeRes;
                    return value ? renderSettingsResourceLabel(value) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/employee-type"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
            columnHelper.accessor("user.gender", {
                header: t("gender"),
                cell: ({ row: { original }}) => {
                    const value = original?.user?.genderRes;
                    return value ? renderSettingsResourceLabel(value) : "--";
                }
            }),
            columnHelper.accessor("user.race", {
                header: t("ethnicity"),
                cell: ({ row: { original }}) => {
                    const value = original?.user?.raceRes;
                    return value ? renderSettingsResourceLabel(value) : "--";
                }
            }),
            columnHelper.accessor("user.employmentType", {
                enableColumnFilter: true,
                header: t("employment-type"),
                cell: ({ row: { original }}) => {
                    const value = original?.user?.employmentTypeRes;
                    return value ? renderSettingsResourceLabel(value) : "--";
                },
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/user-demographic/employment-type"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilters={getSettingsResourceSearchFilters}
                        hasSearch
                    />
                ),
            }),
        ];

        if (groups) {
            const groupColumns = groups.map((group) => {
                const level = `${group.level}`.replace("_", "");
                return columnHelper.accessor(`user.${level}`, {
                    enableColumnFilter: true,
                    header: group.description,
                    cell: ({ row: { original }}) => {
                        const value = original?.user?.[`${level}Res`];
                        return value ? renderOrgLevelResourceLabel(value) : "--";
                    },
                    filterType: "resource-select",
                    Filter: (
                        <ResourceSelect
                            labelPropName="description"
                            resourcePath={`${group.id}/org-levels`}
                            mode="multiple"
                            renderLabel={renderOrgLevelResourceLabel}
                            getSearchFilters={getSettingsOrgResourceSearchFilters}
                            valuePropName="id"
                            hasSearch
                        />
                    ),
                });
            });
            columns.push(...groupColumns);
        }
        if (peopleCustomFields?.length > 0) {
            const customFields = peopleCustomFields.map((customField) => {
                return columnHelper.accessor(`customFields.${customField.code}`, {
                    header: renderSettingsResourceLabel(customField),
                    cell: ({ row: { original } }) => {
                        const value = original?.user?.customFields?.[customField.code];
                        return value ? value : "--";
                    },
                });
            });
            columns.push(...customFields);
        }

        columns.push(
            columnHelper.accessor("startDate", {
                header: t("start-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("endDate", {
                header: t("end-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("paidOnPeriodStartDate", {
                header: t("paid-on-period-start-date"),
                visible: ['applied', 'approved'].includes(selectedStatus),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            columnHelper.accessor("paidOnPeriodEndDate", {
                header: t("paid-on-period-end-date"),
                visible: ['applied', 'approved'].includes(selectedStatus),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "--";
                }
            }),
            // ...(['applied', 'approved'].includes(selectedStatus) ? [
            //     columnHelper.accessor("paidOnPeriodStartDate", {
            //         header: t("paid-on-period-start-date"),
            //         cell: (info) => {
            //             const value = info.getValue();
            //             return moment(value).format(dateFormat);
            //         }
            //     }),
            //     columnHelper.accessor("paidOnPeriodEndDate", {
            //         header: t("paid-on-period-end-date"),
            //         cell: (info) => {
            //             const value = info.getValue();
            //             return moment(value).format(dateFormat);
            //         }
            //     }),
            // ] : []),
            columnHelper.accessor("adjustments", {
                header: t("adjustments"),
                cell: ({ row: { original } }) => {
                    return original?.retros?.length || 0;
                }
            }),
            columnHelper.accessor("status", {
                header: t("status"),
                cell: ({ row: { original } }) => {
                    return <StatusCell original={original} />
                }
            }),
            columnHelper.display({
                header: t("actions"),
                enableHiding: false,
                cell: ({ row: { original } }) => {
                    return (
                        <div className="d-flex gap-3 items-center">
                            <FontAwesomeIcon
                                className="cursor-pointer text-lg"
                                icon={faInfo}
                                onClick={() => openDetailsModal({ data: [original], user: original?.user })}
                            />
                            {(canApprove && original?.status === "pending") && (
                                <>
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-danger text-lg"
                                        title={t("reject-retro-adjustment")}
                                        icon={faClose}
                                        onClick={() => openApproveDenyModal({ ...original, type: "reject" })}
                                    />
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-green text-lg"
                                        title={t("approve-retro-adjustment")}
                                        icon={faCheck}
                                        onClick={() => openApproveDenyModal({ ...original, type: "approve" })}
                                    />
                                </>
                            )}
                            {(canApprove && original?.status === "approved") && (
                                <>
                                    <FontAwesomeIcon
                                        className="cursor-pointer text-orange text-lg"
                                        title={t("reopen-retro-adjustment")}
                                        icon={faRotateLeft}
                                        onClick={() => openApproveDenyModal({ ...original, type: "reopen" })}
                                    />
                                </>
                            )}
                        </div>
                    )
                }
            })
        );

        return columns;
    }, [
        canApprove,
        dateFormat,
        groups,
        openApproveDenyModal,
        openDetailsModal,
        peopleCustomFields,
        selectedStatus,
        t
    ]);
}
